/**
 * AndWatermark - Ant Design Watermark wrapper for Code Canvas
 * Adds watermark to the page or a specific element
 */
import React from "react";
import { Watermark, ConfigProvider, theme } from "antd";
import type { WatermarkProps } from "antd";

export interface AndWatermarkProps extends Omit<WatermarkProps, "children"> {
  /** Watermark text content, can be array for multi-line */
  content?: string | string[];
  /** Width of watermark */
  width?: number;
  /** Height of watermark */
  height?: number;
  /** Rotation angle in degrees */
  rotate?: number;
  /** Z-index of watermark */
  zIndex?: number;
  /** Image URL for image watermark, higher priority than content */
  image?: string;
  /** Gap between watermarks [horizontal, vertical] */
  gap?: [number, number];
  /** Offset of first watermark from top-left [x, y] */
  offset?: [number, number];
  /** Whether to inherit parent's config (5.24.0+) */
  inherit?: boolean;
  /** Font configuration */
  font?: {
    color?: string;
    fontSize?: number;
    fontWeight?: "normal" | "lighter" | "bold" | "bolder" | number;
    fontFamily?: string;
    fontStyle?: "none" | "normal" | "italic" | "oblique";
    textAlign?: "left" | "right" | "center" | "start" | "end";
  };
  /** Design mode flag */
  develop?: boolean;
  /** Design Token configuration */
  token?: Record<string, any>;
  /** Child content to be wrapped with watermark */
  children?: React.ReactNode;
}

/**
 * AndWatermark component
 * Wraps Ant Design Watermark with Design Token support
 */
export const AndWatermark: React.FC<AndWatermarkProps> = ({
  content = "Watermark",
  width,
  height,
  rotate = -22,
  zIndex = 9,
  image,
  gap = [100, 100],
  offset,
  inherit = true,
  font,
  develop,
  token,
  children,
  ...rest
}) => {
  // Parse font configuration
  const fontConfig = {
    color: font?.color ?? "rgba(0, 0, 0, 0.15)",
    fontSize: font?.fontSize ?? 16,
    fontWeight: font?.fontWeight ?? "normal",
    fontFamily: font?.fontFamily ?? "sans-serif",
    fontStyle: font?.fontStyle ?? "normal",
    textAlign: font?.textAlign ?? "center",
  };

  const watermarkElement = (
    <Watermark
      content={content}
      width={width}
      height={height}
      rotate={rotate}
      zIndex={zIndex}
      image={image}
      gap={gap}
      offset={offset}
      inherit={inherit}
      font={fontConfig}
      {...rest}
    >
      {develop ? (
        <div
          data-slot="content"
          style={{
            minHeight: 100,
            minWidth: 100,
            display: "block",
            position: "relative",
            // Ensure watermark overlay is not covered
            // The slot content should not have its own background
          }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </Watermark>
  );

  // Wrap with ConfigProvider if token is provided
  if (token && Object.keys(token).length > 0) {
    return (
      <ConfigProvider
        theme={{
          algorithm: theme.defaultAlgorithm,
          token,
        }}
      >
        {watermarkElement}
      </ConfigProvider>
    );
  }

  return watermarkElement;
};

export default AndWatermark;
